import React, { useState } from 'react';
import { Line, LineChart, XAxis, YAxis, CartesianGrid, ResponsiveContainer } from 'recharts';
import Summary from '@/components/Summary';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import {
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartTooltip
} from '@/components/ui/chart';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import CustomTooltip from '@/components/charts/CustomTooltip';
import { CircleAlert } from 'lucide-react';
import { CopilotValue9, CopilotValue9ProductInfo } from '@/stores/types/copilotValue/copilotValue9';
import { interpolate } from '@/lib/utils';

interface PerformanceComparisonProps {
  selected: boolean;
  selectChange: (type: string, checked: boolean) => void;
  totalData: CopilotValue9;
  type: string;
}

const PerformanceComparison: React.FC<PerformanceComparisonProps> = ({
  selected = true,
  selectChange,
  totalData,
  type
}) => {
  const productInfo = totalData?.product_info || ([] as CopilotValue9ProductInfo[]);
  const product1 = productInfo[0] || ({} as CopilotValue9ProductInfo);
  const product2 = productInfo[1] || ({} as CopilotValue9ProductInfo);

  const chartConfig = {
    新能源: {
      label: '新能源',
      color: '#5959EB'
    },
    消费: {
      label: '消费',
      color: '#FA8C16'
    }
  };

  const mockDataValue = () => (Math.random() * 200 - 100).toFixed(2);
  // 图表数据
  const chartDataMoack = [
    { month: '起始', 新能源: mockDataValue(), 消费: mockDataValue() },
    { month: '1月', 新能源: mockDataValue(), 消费: mockDataValue() },
    { month: '2月', 新能源: mockDataValue(), 消费: mockDataValue() },
    { month: '3月', 新能源: mockDataValue(), 消费: mockDataValue() },
    { month: '6月', 新能源: mockDataValue(), 消费: mockDataValue() },
    { month: '1年', 新能源: mockDataValue(), 消费: mockDataValue() }
  ];

  const performanceDataMoack = chartDataMoack.map(item => {
    const newEnergyValue = Number(item.新能源);
    const consumptionValue = Number(item.消费);
    return {
      period: item.month,
      新能源: newEnergyValue === 0 ? 0 : newEnergyValue > 0 ? `+${item.新能源}` : item.新能源,
      消费: consumptionValue === 0 ? 0 : consumptionValue > 0 ? `+${item.消费}` : item.新能源,
      差值: interpolate(newEnergyValue, consumptionValue).toNumber().toFixed(2)
    };
  });

  const totalValue = { 新能源: 0, 消费: 0, 差值: 0 };
  chartDataMoack.forEach((item, index) => {
    totalValue.新能源 += Number(item.新能源);
    totalValue.消费 += Number(item.消费);
    totalValue.差值 += Number(performanceDataMoack[index].差值);
  });

  performanceDataMoack.push({
    period: '成立以来',
    新能源:
      totalValue.新能源 === 0
        ? '0'
        : totalValue.新能源 > 0
        ? `+${totalValue.新能源.toFixed(2)}`
        : totalValue.新能源.toFixed(2),
    消费:
      totalValue.消费 === 0
        ? '0'
        : totalValue.消费 > 0
        ? `+${totalValue.消费.toFixed(2)}`
        : totalValue.消费.toFixed(2),
    差值:
      totalValue.差值 === 0
        ? '0'
        : totalValue.差值 > 0
        ? `+${totalValue.差值.toFixed(2)}`
        : totalValue.差值.toFixed(2)
  });

  const [chartData, setChartData] = useState(chartDataMoack);
  const [performanceData, setPerformanceData] = useState(performanceDataMoack);

  // 收益数据

  // const timeRange = '本年收益';

  const handleValueChange = (value: string) => {
    let mockData = null;
    if (value === 'last1Month') {
      mockData = [
        { month: '起始', 新能源: mockDataValue(), 消费: mockDataValue() },
        { month: '1周', 新能源: mockDataValue(), 消费: mockDataValue() },
        { month: '2周', 新能源: mockDataValue(), 消费: mockDataValue() },
        { month: '3周', 新能源: mockDataValue(), 消费: mockDataValue() },
        { month: '1月', 新能源: mockDataValue(), 消费: mockDataValue() }
      ];
    } else if (value === 'last3Month') {
      mockData = [
        { month: '起始', 新能源: mockDataValue(), 消费: mockDataValue() },
        { month: '1月', 新能源: mockDataValue(), 消费: mockDataValue() },
        { month: '2月', 新能源: mockDataValue(), 消费: mockDataValue() },
        { month: '3月', 新能源: mockDataValue(), 消费: mockDataValue() }
      ];
    } else if (value === 'last6Month') {
      mockData = [
        { month: '起始', 新能源: mockDataValue(), 消费: mockDataValue() },
        { month: '2月', 新能源: mockDataValue(), 消费: mockDataValue() },
        { month: '4月', 新能源: mockDataValue(), 消费: mockDataValue() },
        { month: '6月', 新能源: mockDataValue(), 消费: mockDataValue() }
      ];
    } else if (value === 'last1Year') {
      mockData = [
        { month: '起始', 新能源: mockDataValue(), 消费: mockDataValue() },
        { month: '1月', 新能源: mockDataValue(), 消费: mockDataValue() },
        { month: '2月', 新能源: mockDataValue(), 消费: mockDataValue() },
        { month: '3月', 新能源: mockDataValue(), 消费: mockDataValue() },
        { month: '6月', 新能源: mockDataValue(), 消费: mockDataValue() },
        { month: '1年', 新能源: mockDataValue(), 消费: mockDataValue() }
      ];
    }

    setChartData(mockData);
    const performanceData = mockData.map(item => {
      const newEnergyValue = Number(item.新能源);
      const consumptionValue = Number(item.消费);
      const differenceValue = newEnergyValue - consumptionValue;
      return {
        period: item.month,
        新能源:
          newEnergyValue === 0 ? 0 : newEnergyValue > 0 ? `+${item.新能源}` : `${item.新能源}`,
        消费: consumptionValue === 0 ? 0 : consumptionValue > 0 ? `+${item.消费}` : `${item.消费}`,
        差值:
          differenceValue === 0
            ? 0
            : differenceValue > 0
            ? `+${differenceValue.toFixed(2)}`
            : `${differenceValue.toFixed(2)}`
      };
    });
    const totalValue = { 新能源: 0, 消费: 0, 差值: 0 };
    mockData.forEach((item, index) => {
      totalValue.新能源 += Number(item.新能源);
      totalValue.消费 += Number(item.消费);
      totalValue.差值 += Number(performanceData[index].差值);
    });
    performanceData.push({
      period: '成立以来',
      新能源:
        totalValue.新能源 === 0
          ? '0'
          : totalValue.新能源 > 0
          ? `+${totalValue.新能源.toFixed(2)}`
          : `${totalValue.新能源.toFixed(2)}`,
      消费:
        totalValue.消费 === 0
          ? '0'
          : totalValue.消费 > 0
          ? `+${totalValue.消费.toFixed(2)}`
          : `${totalValue.消费.toFixed(2)}`,
      差值:
        totalValue.差值 === 0
          ? '0'
          : totalValue.差值 > 0
          ? `+${totalValue.差值.toFixed(2)}`
          : `${totalValue.差值.toFixed(2)}`
    });
    setPerformanceData(performanceData);
  };

  return (
    <div
      className="mb-3 flex flex-col gap-3 rounded-lg border border-gray-200 bg-white p-3"
      data-chart="performanceComparisonChart"
    >
      {/* 标题行 */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="custom-checkbox">
            <input
              type="checkbox"
              checked={selected}
              onChange={e => selectChange?.(type, e.target.checked)}
              className="h-4 w-4"
              id="customcheckbox19"
            />
            <label htmlFor="customcheckbox19" />
          </div>
          <span className="text-base font-semibold text-gray-900">收益表现对比</span>
        </div>
        <div className="flex items-center gap-2">
          <Select onValueChange={handleValueChange} defaultValue="last1Year">
            <SelectTrigger className="h-auto w-auto border-0 bg-transparent p-0 shadow-none focus:ring-0 focus:ring-offset-0">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="border-gray-200 bg-white shadow-md">
              <SelectItem value="last1Month">近1月</SelectItem>
              <SelectItem value="last3Month">近3月</SelectItem>
              <SelectItem value="last6Month">近半年</SelectItem>
              <SelectItem value="last1Year">近1年</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* 图表区域 */}
      <div className="flex flex-col gap-3">
        {/* 图表 */}
        <div className="flex flex-col gap-1" data-chart="performanceComparisonChart-old-chart">
          <div className="relative h-[178px] ">
            <ResponsiveContainer>
              <ChartContainer config={chartConfig}>
                <LineChart data={chartData}>
                  <CartesianGrid vertical={false} />
                  <XAxis
                    dataKey="month"
                    tick={{ fontSize: 10, fill: '#595959' }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <YAxis
                    domain={[-100, 100]}
                    tick={{ fontSize: 10, fill: '#595959' }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <Line
                    type="monotone"
                    dataKey="新能源"
                    stroke="#5959EB"
                    strokeWidth={3}
                    dot={false}
                    activeDot={{ r: 4, stroke: '#5959EB', strokeWidth: 2 }}
                  />
                  <Line
                    type="monotone"
                    dataKey="消费"
                    stroke="#FA8C16"
                    strokeWidth={3}
                    dot={false}
                    activeDot={{ r: 4, stroke: '#FA8C16', strokeWidth: 2 }}
                  />
                  <ChartTooltip
                    cursor={false}
                    content={CustomTooltip}
                    formatter={(value: any) => [`${Number(value).toFixed(2)}`]}
                  />
                  <ChartLegend content={<ChartLegendContent />} />
                </LineChart>
              </ChartContainer>
            </ResponsiveContainer>
          </div>
        </div>

        {/* 收益数据表格 */}
        <div className="overflow-hidden rounded-lg border border-gray-200">
          <div className="flex">
            {/* 时间段列 */}
            <div className="flex w-full flex-col">
              <div className="rounded-tl-lg bg-[#F5F5F5] px-2 py-1">
                <span className="text-xs font-semibold text-gray-900">时间段</span>
              </div>
              {performanceData.map((item, index) => (
                <div key={index} className="bg-white px-2 py-1">
                  <span className="text-xs text-gray-900">{item.period}</span>
                </div>
              ))}
            </div>

            {/* 招商新能源列 */}
            <div className="flex w-full min-w-0 flex-col">
              <div className="truncate bg-[#5959EB1A] px-2 py-1">
                <span className="text-xs font-semibold text-blue-600" title={product1.产品名称}>
                  {product1.产品名称 || '无'}
                </span>
              </div>
              {performanceData.map((item, index) => (
                <div key={index} className="bg-[#F2F7FF80] px-2 py-1">
                  <span
                    className={`text-xs font-semibold ${
                      String(item.新能源 || 0).startsWith('+')
                        ? 'text-red-600'
                        : Number(item.新能源) !== 0
                        ? 'text-green-600'
                        : ''
                    }`}
                  >
                    {item.新能源}
                  </span>
                </div>
              ))}
            </div>

            {/* 易方达消费列 */}
            <div className="flex w-full min-w-0 flex-col">
              <div className="truncate bg-[#FA8C161A] px-2 py-1">
                <span className="text-xs font-semibold text-orange-600" title={product2.产品名称}>
                  {product2.产品名称 || '无'}
                </span>
              </div>
              {performanceData.map((item, index) => (
                <div key={index} className="bg-[#FFF7E699] px-2 py-1">
                  <span
                    className={`text-xs font-semibold ${
                      String(item.消费 || 0).startsWith('+') ? 'text-red-600' : 'text-green-600'
                    }`}
                  >
                    {item.消费}
                  </span>
                </div>
              ))}
            </div>

            {/* 差值列 */}
            <div className="flex w-full flex-col rounded-tr-lg">
              <div className="flex items-center justify-center rounded-tr-lg bg-[#2F54EB0A] p-[8px]">
                <span className="mr-1 text-xs font-semibold text-blue-600">差值</span>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className="cursor-help overflow-hidden text-ellipsis text-sm font-normal text-[#666666]">
                        <CircleAlert size={14} className="text-blue-600" />
                      </span>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p className="max-w-xs">差值</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
              {performanceData.map((item, index) => (
                <div key={index} className="bg-[#E6F4FF99] px-2 py-1">
                  <span
                    className={`text-xs font-semibold ${
                      String(item.差值 || 0).startsWith('+') ? 'text-red-600' : 'text-green-600'
                    }`}
                  >
                    {item.差值}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* AI总结部分 */}
      <Summary title="AI总结-历史业绩" content={totalData?.历史业绩} isShowSuggestions={false} />
    </div>
  );
};

export default PerformanceComparison;
